import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';

const DOMAINS = [
  { id: 'patient_care',      title: 'Patient Care / Clinical Skills' },
  { id: 'medical_knowledge', title: 'Medical Knowledge' },
  { id: 'communication',     title: 'Interpersonal & Communication Skills' },
  { id: 'practice_learning', title: 'Practice-Based Learning Skills' },
  { id: 'system_practice',   title: 'System-Based Practice Skills' },
  { id: 'professionalism',   title: 'Professionalism' },
];

const scoreColor = (n) => !n ? '#888' : n <= 4 ? '#E85252' : n <= 6 ? '#F4A83A' : n <= 8 ? '#4A9D6F' : '#1A3A5C';
const scoreLabel = (n) => !n ? '—' : n <= 4 ? 'Unsatisfactory' : n <= 6 ? 'Satisfactory' : n <= 8 ? 'Good' : 'Outstanding';
const pctColor = (p) => p >= 90 ? '#1A3A5C' : p >= 70 ? '#4A9D6F' : p >= 50 ? '#F4A83A' : '#E85252';

const printEvaluation = (e) => {
  const pct = Math.round(((e.total_score || 0) / 60) * 100);
  const domainRows = DOMAINS.map(d => `
    <tr>
      <td style="padding:10px 12px;border:1px solid #ddd;font-size:13px;color:#333;">${d.title}</td>
      <td style="padding:10px 12px;border:1px solid #ddd;text-align:center;font-size:15px;font-weight:700;color:${scoreColor(e[`score_${d.id}`])};">${e[`score_${d.id}`] || '—'}</td>
      <td style="padding:10px 12px;border:1px solid #ddd;font-size:12px;color:#666;font-style:italic;">${e[`comment_${d.id}`] || ''}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Evaluation — ${e.fellow_name}</title>
  <style>
    body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1A1A1F; }
    @media print { body { margin: 20px; } .no-print { display: none; } }
    h1 { font-size: 20px; color: #1A3A5C; margin-bottom: 4px; }
    .subtitle { font-size: 13px; color: #888; margin-bottom: 24px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; border: 1px solid #ddd; padding: 16px; border-radius: 6px; }
    .info-item label { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #999; margin-bottom: 4px; }
    .info-item value { font-size: 14px; font-weight: 600; color: #1A1A1F; }
    .scale { background: #EEF3FA; padding: 10px 14px; border-radius: 6px; font-size: 12px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { background: #1A3A5C; color: #fff; padding: 10px 12px; text-align: left; font-size: 12px; }
    .total { background: #1A3A5C; color: #fff; padding: 16px 20px; border-radius: 6px; display: flex; justify-content: space-between; margin-bottom: 24px; }
    .total-val { font-size: 36px; font-weight: 700; }
    .sig-block { margin-top: 32px; border-top: 1px solid #ddd; padding-top: 20px; }
    .sig-row { display: flex; gap: 40px; margin-bottom: 20px; }
    .sig-line { flex: 1; border-bottom: 1px solid #333; padding-bottom: 4px; margin-bottom: 4px; min-height: 30px; font-size: 13px; }
    .sig-label { font-size: 11px; color: #888; }
    .ack { background: #F0FFF6; border: 1px solid #A8DCC0; padding: 12px 16px; border-radius: 6px; margin-top: 16px; font-size: 13px; color: #1E5C40; }
  </style>
</head>
<body>
  <div style="text-align:center;margin-bottom:20px;">
    <div style="font-size:12px;color:#888;">Academic Affairs Office · Physician Education Center</div>
    <h1>Physician Trainee General Evaluation Form</h1>
    <div class="subtitle">Department of Hospice and Palliative Medicine @ KHCC</div>
  </div>

  <div class="info-grid">
    <div class="info-item"><label>Trainee's Name</label><value>${e.fellow_name}</value></div>
    <div class="info-item"><label>Attending Name</label><value>${e.evaluator_name}</value></div>
    <div class="info-item"><label>Evaluation Period</label><value>${e.rotation_period || '—'}</value></div>
    <div class="info-item"><label>Evaluation Date</label><value>${e.evaluation_date}</value></div>
  </div>

  <div class="scale">
    <strong>Evaluation Scale:</strong> &nbsp;
    NA = Not Applicable &nbsp;|&nbsp; 1–4 = Unsatisfactory &nbsp;|&nbsp; 5–6 = Satisfactory &nbsp;|&nbsp; 7–8 = Good &nbsp;|&nbsp; 9–10 = Outstanding
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:55%">Domain</th>
        <th style="width:15%;text-align:center;">Score (/10)</th>
        <th style="width:30%">Comments</th>
      </tr>
    </thead>
    <tbody>${domainRows}</tbody>
  </table>

  <div class="total">
    <div><div style="font-size:11px;letter-spacing:1px;opacity:0.7;margin-bottom:4px;">TOTAL SCORE</div><div class="total-val">${e.total_score || 0}/60</div></div>
    <div style="text-align:right;"><div style="font-size:11px;letter-spacing:1px;opacity:0.7;margin-bottom:4px;">PERCENTAGE</div><div class="total-val">${pct}%</div><div style="font-size:13px;opacity:0.8;margin-top:4px;">${pct >= 90 ? 'Outstanding' : pct >= 70 ? 'Good' : pct >= 50 ? 'Satisfactory' : 'Unsatisfactory'}</div></div>
  </div>

  <div class="sig-block">
    <div class="sig-row">
      <div><div class="sig-line">${e.evaluator_name}</div><div class="sig-label">Attending Physician — Date: ${e.evaluation_date}</div></div>
      <div><div class="sig-line"></div><div class="sig-label">Program Director — Date: _______________</div></div>
    </div>
    <div class="sig-row">
      <div><div class="sig-line">${e.acknowledgment_status === 'acknowledged' ? e.fellow_name : ''}</div><div class="sig-label">Trainee — Date: ${e.acknowledged_at ? new Date(e.acknowledged_at).toLocaleDateString() : '_______________'}</div></div>
      <div style="flex:1"><div class="sig-line">${e.fellow_comments || ''}</div><div class="sig-label">Trainee's Comments</div></div>
    </div>
    ${e.acknowledgment_status === 'acknowledged' ? `<div class="ack">✓ Acknowledged by trainee on ${new Date(e.acknowledged_at).toLocaleDateString()}</div>` : ''}
  </div>

  <button class="no-print" onclick="window.print()" style="margin-top:24px;padding:12px 24px;background:#1A3A5C;color:#fff;border:none;border-radius:8px;font-size:14px;cursor:pointer;">🖨 Print / Save as PDF</button>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
};

export default function EvaluationHistory({ isMobile }) {
  const [evaluations, setEvaluations] = useState([]);
  const [fellows, setFellows]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterFellow, setFilterFellow] = useState('all');
  const [filterQuarter, setFilterQuarter] = useState('all');
  const [selected, setSelected]       = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [{ data: evals }, { data: profiles }] = await Promise.all([
      supabase.from('evaluations').select('*').order('evaluation_date', { ascending: false }),
      supabase.from('profiles').select('id, full_name').eq('role', 'fellow'),
    ]);
    setEvaluations(evals || []);
    setFellows(profiles || []);
    setLoading(false);
  };

  const quarters = ['Q1 (July – September)', 'Q2 (October – December)', 'Q3 (January – March)', 'Q4 (April – June)'];

  const filtered = evaluations
    .filter(e => filterFellow === 'all' || e.fellow_id === filterFellow)
    .filter(e => filterQuarter === 'all' || e.rotation_period === filterQuarter);

  // Performance trend per fellow
  const fellowTrend = (fellowId) => {
    return evaluations
      .filter(e => e.fellow_id === fellowId)
      .sort((a, b) => new Date(a.evaluation_date) - new Date(b.evaluation_date))
      .map(e => ({ quarter: e.rotation_period, pct: Math.round(((e.total_score || 0) / 60) * 100), date: e.evaluation_date }));
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading evaluations…</div>;

  if (selected) {
    const e = selected;
    const pct = Math.round(((e.total_score || 0) / 60) * 100);
    return (
      <div style={{ background: '#F7F4F0', minHeight: '100vh', paddingBottom: isMobile ? 80 : 40 }}>
        <div style={{ background: '#1A3A5C', padding: isMobile ? '14px 16px' : '16px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#7EB8D4', fontSize: 14, cursor: 'pointer', padding: 0 }}>← Back</button>
            <span style={{ fontFamily: "'DM Serif Display'", fontSize: isMobile ? 15 : 18, color: '#F5F0E8' }}>Evaluation Detail</span>
          </div>
          <button onClick={() => printEvaluation(e)}
            style={{ padding: '8px 16px', background: '#7EB8D4', color: '#1A3A5C', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans'" }}>
            🖨 Print / PDF
          </button>
        </div>

        <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? 16 : '28px 40px' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', borderTop: '4px solid #1A3A5C' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#888' }}>Academic Affairs Office · Physician Education Center</div>
              <div style={{ fontFamily: "'DM Serif Display'", fontSize: 18, color: '#1A3A5C', marginTop: 4 }}>Physician Trainee General Evaluation Form</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: 12, borderTop: '1px solid #F0EDE8', paddingTop: 16 }}>
              {[{label:'Trainee',val:e.fellow_name},{label:'Attending',val:e.evaluator_name},{label:'Period',val:e.rotation_period||'—'},{label:'Date',val:e.evaluation_date}].map((r,i)=>(
                <div key={i}><div style={{ fontSize:10,color:'#999',textTransform:'uppercase',letterSpacing:0.5,marginBottom:4 }}>{r.label}</div><div style={{ fontSize:14,fontWeight:600,color:'#1A1A1F' }}>{r.val}</div></div>
              ))}
            </div>
          </div>

          {DOMAINS.map((domain, idx) => {
            const score = e[`score_${domain.id}`];
            const comment = e[`comment_${domain.id}`];
            return (
              <div key={domain.id} style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'DM Serif Display'", fontSize: 14, color: '#1A3A5C' }}>{idx+1}. {domain.title}</div>
                  {comment && <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic', marginTop: 4 }}>"{comment}"</div>}
                </div>
                <div style={{ textAlign: 'center', minWidth: 64 }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: scoreColor(score), display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px' }}>
                    <span style={{ fontFamily: "'DM Serif Display'", fontSize: 20, color: '#fff', fontWeight: 700 }}>{score}</span>
                  </div>
                  <div style={{ fontSize: 10, color: scoreColor(score), fontWeight: 600 }}>{scoreLabel(score)}</div>
                </div>
              </div>
            );
          })}

          <div style={{ background: '#1A3A5C', borderRadius: 12, padding: '18px 24px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div><div style={{ fontSize:11,color:'#7EB8D4',textTransform:'uppercase',letterSpacing:1,marginBottom:4 }}>Total Score</div><div style={{ fontFamily:"'DM Serif Display'",fontSize:40,color:'#F5F0E8' }}>{e.total_score||0}<span style={{ fontSize:18,opacity:0.5 }}>/60</span></div></div>
            <div style={{ textAlign:'right' }}><div style={{ fontSize:11,color:'#7EB8D4',textTransform:'uppercase',letterSpacing:1,marginBottom:4 }}>Percentage</div><div style={{ fontFamily:"'DM Serif Display'",fontSize:40,color:'#F5F0E8' }}>{pct}<span style={{ fontSize:18,opacity:0.5 }}>%</span></div><div style={{ fontSize:12,fontWeight:600,marginTop:4,color:pct>=90?'#7EB8D4':pct>=70?'#A8DCC0':pct>=50?'#F4DFA8':'#F4A8A8' }}>{pct>=90?'★ Outstanding':pct>=70?'✓ Good':pct>=50?'~ Satisfactory':'✗ Unsatisfactory'}</div></div>
          </div>

          {e.acknowledgment_status === 'acknowledged'
            ? <div style={{ background:'#F0FFF6',border:'1px solid #A8DCC0',borderRadius:10,padding:'14px 18px' }}>
                <div style={{ fontSize:14,fontWeight:600,color:'#1E5C40',marginBottom:4 }}>✓ Acknowledged by trainee</div>
                <div style={{ fontSize:13,color:'#555' }}>Signed on {new Date(e.acknowledged_at).toLocaleDateString()}</div>
                {e.fellow_comments && <div style={{ fontSize:13,color:'#555',marginTop:6,fontStyle:'italic' }}>Trainee comments: "{e.fellow_comments}"</div>}
              </div>
            : <div style={{ background:'#FFF8EE',border:'1px solid #F4DFA8',borderRadius:10,padding:'14px 18px',fontSize:13,color:'#8A6B2A' }}>⏳ Pending trainee acknowledgment</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : '36px 40px', paddingBottom: isMobile ? 80 : 40 }}>
      <h1 style={{ fontFamily: "'DM Serif Display'", fontSize: isMobile ? 22 : 28, color: '#1A1A1F', marginBottom: 4 }}>Evaluation Records</h1>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>{evaluations.length} evaluations total</p>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={filterFellow} onChange={e => setFilterFellow(e.target.value)}
          style={{ padding: '9px 13px', border: '1.5px solid #E0DDD8', borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans'", background: '#fff', color: '#1A1A1F', outline: 'none', minWidth: 180 }}>
          <option value='all'>All Fellows</option>
          {fellows.map(f => <option key={f.id} value={f.id}>{f.full_name}</option>)}
        </select>
        <select value={filterQuarter} onChange={e => setFilterQuarter(e.target.value)}
          style={{ padding: '9px 13px', border: '1.5px solid #E0DDD8', borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans'", background: '#fff', color: '#1A1A1F', outline: 'none', minWidth: 180 }}>
          <option value='all'>All Quarters</option>
          {quarters.map(q => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>

      {/* Performance trend cards per fellow */}
      {filterFellow === 'all' && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: "'DM Serif Display'", fontSize: 16, color: '#1A1A1F', marginBottom: 14 }}>Performance Overview by Fellow</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 12 }}>
            {fellows.filter(f => evaluations.some(e => e.fellow_id === f.id)).map(fellow => {
              const trend = fellowTrend(fellow.id);
              const latest = trend[trend.length - 1];
              const avg = trend.length ? Math.round(trend.reduce((s, t) => s + t.pct, 0) / trend.length) : 0;
              return (
                <div key={fellow.id} style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1F' }}>{fellow.full_name}</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{trend.length} evaluation{trend.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'DM Serif Display'", fontSize: 26, color: pctColor(avg) }}>{avg}%</div>
                      <div style={{ fontSize: 10, color: '#999' }}>avg</div>
                    </div>
                  </div>
                  {/* Mini trend bars */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 40 }}>
                    {trend.map((t, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div style={{ width: '100%', background: pctColor(t.pct), borderRadius: '3px 3px 0 0', height: `${Math.max(4, (t.pct / 100) * 32)}px` }} />
                        <div style={{ fontSize: 8, color: '#999', textAlign: 'center' }}>{t.quarter?.split(' ')[0]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Evaluations list */}
      {filtered.length === 0
        ? <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa' }}>No evaluations found.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(e => {
              const pct = Math.round(((e.total_score || 0) / 60) * 100);
              const isAck = e.acknowledgment_status === 'acknowledged';
              return (
                <div key={e.id} style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '12px 14px' : '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `3px solid ${pctColor(pct)}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setSelected(e)}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#2D6A8A' }}>{e.fellow_name}</span>
                        <span style={{ fontSize: 11, color: '#999' }}>{e.evaluation_date}</span>
                        <span style={{ fontSize: 11, background: '#EEF3FA', color: '#2D6A8A', padding: '2px 8px', borderRadius: 20, fontWeight: 500 }}>{e.rotation_period}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#888' }}>Evaluated by {e.evaluator_name}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: isAck ? '#F0FFF6' : '#FFF8EE', color: isAck ? '#1E5C40' : '#C4622A', border: `1px solid ${isAck ? '#A8DCC0' : '#F4DFA8'}` }}>
                          {isAck ? '✓ Acknowledged' : '⏳ Pending acknowledgment'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, marginLeft: 12 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontFamily: "'DM Serif Display'", fontSize: 26, color: pctColor(pct) }}>{pct}%</div>
                        <div style={{ fontSize: 10, color: '#999' }}>{e.total_score}/60</div>
                      </div>
                      <button onClick={() => printEvaluation(e)}
                        style={{ padding: '5px 10px', background: '#EEF3FA', color: '#1A3A5C', border: '1px solid #A8C8DC', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'", whiteSpace: 'nowrap' }}>
                        🖨 Print
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}
    </div>
  );
}
